import React, { useState } from "react";
import { toast } from "sonner";
import { X, Send } from "lucide-react";
import { http } from "@/lib/api";

export default function ReassignRequestModal({ open, onClose, lead, advisors, onFiled }) {
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const names = advisors.map((a) => (typeof a === "string" ? a : a.name)).filter((n) => n !== lead.advisor);

  const submit = async () => {
    if (!to) return toast.error("Pick a new advisor");
    setBusy(true);
    try {
      await http.post(`/leads/${lead.id}/reassign-request`, { to_advisor: to, reason });
      toast.success("Reassignment request filed — awaiting Ops approval");
      onFiled?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not file request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="reassign-request-modal"
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="font-serif text-xl">Request reassignment</h2>
            <p className="text-xs text-slate-500 mt-0.5">Lead: {lead.name} · currently with {lead.advisor || "—"}</p>
          </div>
          <button onClick={onClose} data-testid="reassign-close-btn">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-slate-500">New advisor</span>
            <select
              data-testid="reassign-to-select"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm border border-slate-200 bg-white"
            >
              <option value="">Select…</option>
              {names.map((n) => <option key={n}>{n}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-slate-500">Reason</span>
            <textarea
              data-testid="reassign-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Why does this lead need a different advisor?"
              className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm border border-slate-200"
            />
          </label>
          <p className="text-[11px] text-slate-500">
            This request will be reviewed by Ops first, then finalised by a Founder.
          </p>
        </div>
        <div className="px-6 py-4 bg-slate-50 border-t flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white hover:bg-slate-100 px-4 py-2 text-sm font-semibold"
          >
            Cancel
          </button>
          <button
            data-testid="reassign-submit-btn"
            onClick={submit}
            disabled={busy || !to}
            className="rounded-xl bg-[#0B1E3F] hover:bg-[#162A4D] text-white px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Send className="w-4 h-4" /> {busy ? "Filing…" : "File request"}
          </button>
        </div>
      </div>
    </div>
  );
}
